import * as React from 'react';

import {
  DialogClosingConfirmation,
  EntityDetails,
  Loading,
  LocalizedDate,
  Row,
  cn,
} from '@rematter/ui-web';
import { ElementRect, useElementRect, useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/loadDetail.state';
import {
  $isLoadCreateMode,
  $lazyBookingPhotoRequirements,
  $photoRequirementsCount,
  $photoUploadStatus,
  $syncAndSaveLoad,
  $uploadedPhotosCount,
} from '../../_state/loadDetail.state';
import {
  $discardBaseline,
  $hasUnsavedChanges,
  $resetLoadFormToLoad,
  $showSaveSuccess,
  $tagToLineItemMap,
  $updateLoadForm,
} from '../../_state/loadForm.state';
import { $loadHeaderRect } from '../../_state/loadStaged.state';
import { usePopulateLoadForm } from '../../hooks/usePopulateLoadForm';
import { tradeRoutePaths } from '../../routes';
import { LoadDetailsTab } from '../../types/load';
import { handleActionableError } from '../../utils/errorHandling';
import {
  LoadFormTab,
  extractDatesAndEntitiesFromLoad,
  transformLoadFormToInput,
  transformLoadFormToSalesOrderUpdateParams,
} from '../../utils/load';

import { useSyncSalesOrderLineItems } from './hooks/useSyncSalesOrderLineItems';
import { LoadStatusTransition } from './LoadStatusTransition';
import { LoadTabDocuments } from './LoadTabDocuments';
import { LoadTabMaterialsInfo } from './LoadTabMaterialsInfo';
import { LoadTabNotes } from './LoadTabNotes';
import { LoadTabPhotos } from './LoadTabPhotos';
import { LoadTabShipmentInfo } from './LoadTabShipmentInfo';
import { MaterialsTabSize } from './MaterialsTabSize';
import { RemoveOrphanedLinkDialog } from './RemoveOrphanedLinkDialog';

import { SalesRootLayout } from '~/modules/trade/_components/_layouts/SalesRootLayout';
import {
  BookingBadge,
  LoadStatusBadge,
  SalesOrderBadge,
} from '~/modules/trade/_components/_shared/StatusBadges';
import { bookingSalesOrderDeleteAtom } from '~/modules/trade/_graphql/mutationBookingSalesOrderDelete';
import {
  containerLoadAtom,
  containerUnloadAtom,
} from '~/modules/trade/_graphql/mutationContainerTransitions';
import { orphanedLinkAtom } from '~/modules/trade/_state/orphanedLinkState';
import {
  DefaultTranslations,
  useTranslations,
} from '~/modules/trade/i18n/useTranslations';
import { LoadFormState, LoadRecord } from '~/modules/trade/types/load';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const notificationBannerByLoadStatus = ({
  load,
  t,
}: {
  load: LoadRecord | null | undefined;
  t: DefaultTranslations;
}) => {
  if (!load) return null;

  if (load.status === 'Reconciled') {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.loadDetail.notificationBanner({
            status: (
              <span className="font-bold">
                {t.loadDetail.statusLowercase.reconciled}
              </span>
            ),
            date: (
              <span className="font-bold">
                <LocalizedDate
                  date={load.timestamps.updatedAt ?? load.timestamps.createdAt}
                />
              </span>
            ),
          })}
        </div>
      ),
      type: 'success' as const,
    };
  }

  if (load.status === 'Voided') {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.loadDetail.notificationBanner({
            status: (
              <span className="font-bold">
                {t.loadDetail.statusLowercase.voided}
              </span>
            ),
            date: (
              <span className="font-bold">
                <LocalizedDate
                  date={load.timestamps.updatedAt ?? load.timestamps.createdAt}
                />
              </span>
            ),
          })}
        </div>
      ),
      type: 'failure' as const,
    };
  }

  return null;
};

export const LoadDetailView = () => {
  const toast = useToast();
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const [, resetForm] = useAtom($resetLoadFormToLoad);

  const updateLoadForm = useSetAtom($updateLoadForm);
  const syncAndSaveLoad = useSetAtom($syncAndSaveLoad);
  const setLoadHeaderRect = useSetAtom($loadHeaderRect);
  const tagToLineItemMap = useAtomValue($tagToLineItemMap);

  const load = useAtomValue(state.$loadData);
  const loadId = useAtomValue(state.$loadId);
  const isCreateMode = useAtomValue($isLoadCreateMode);
  const isLoading = useAtomValue(state.$isLoadLoading);
  const error = useAtomValue(state.$loadError);
  const [tab, setTab] = useAtom(state.$tab);

  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<LoadDetailsTab | null>(null);

  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);
  const [discardBaseline, setDiscardBaseline] = useAtom($discardBaseline);
  const setShowSaveSuccess = useSetAtom($showSaveSuccess);

  // Orphaned link detection
  const [orphanedLink, setOrphanedLink] = useAtom(orphanedLinkAtom);
  const deleteRelationship = useSetAtom(bookingSalesOrderDeleteAtom());

  // Container linking mutations
  const linkContainerToLoad = useSetAtom(containerLoadAtom());
  const unlinkContainerFromLoad = useSetAtom(containerUnloadAtom());

  // 🟠🟠🟠 Populate form with existing load data
  usePopulateLoadForm({ load });

  const {
    isSyncing: isSyncingSalesOrderLineItems,
    salesOrderLineItems,
    queriedSalesOrderId,
  } = useSyncSalesOrderLineItems({
    loadSalesOrderId: load?.salesOrderId,
  });

  // Photo requirements state
  const {
    data: photoRequirementData,
    fetch: fetchPhotoRequirements,
    isFetched: isPhotoRequirementFetched,
  } = useAtomValue($lazyBookingPhotoRequirements);
  const {
    data: loadPhotos,
    fetch: fetchLoadPhotos,
    isFetched: isLoadPhotosFetched,
  } = useAtomValue(state.$lazyLoadPhotosWithoutUrl);

  const photoRequirementsCount = useAtomValue($photoRequirementsCount);
  const uploadedPhotosCount = useAtomValue($uploadedPhotosCount);
  const photoUploadStatus = useAtomValue($photoUploadStatus);

  // Fetch photo requirements when bookingId changes
  React.useEffect(() => {
    if (
      load?.bookingId &&
      !photoRequirementData &&
      !isPhotoRequirementFetched
    ) {
      fetchPhotoRequirements({ bookingId: load.bookingId });
    }
  }, [
    load?.bookingId,
    fetchPhotoRequirements,
    photoRequirementData,
    isPhotoRequirementFetched,
  ]);

  React.useEffect(() => {
    if (loadId && !isCreateMode && !loadPhotos && !isLoadPhotosFetched) {
      fetchLoadPhotos({ id: loadId });
    }
  }, [loadId, isCreateMode, fetchLoadPhotos, isLoadPhotosFetched, loadPhotos]);

  const handleBack = React.useCallback(() => {
    navigate(tradeRoutePaths.loads);
  }, [navigate]);

  const handleDiscard = React.useCallback(
    (onlyDiscardDatesAndEntities?: boolean) => {
      // Reset the form to the current load data from the backend
      if (load) {
        if (!onlyDiscardDatesAndEntities) return resetForm(load);

        // Reset only the dates, facilityId, salesOrderId and bookingId from the load
        updateLoadForm(extractDatesAndEntitiesFromLoad(load));
        toast.success(t.loadDetail.messages.changesDiscarded);
      }
    },
    [load, toast, t, resetForm, updateLoadForm],
  );

  const handleSaveUpdates = React.useCallback(
    async (
      tab?: LoadFormTab,
      data?: LoadFormState,
      onlyDatesAndEntities?: boolean,
    ) => {
      if (!loadId || !data) return;

      try {
        setIsUpdating(true);

        // Use syncAndSaveLoad which handles syncing line items if salesOrderId changed
        const updatedForm = await syncAndSaveLoad({
          loadId,
          persistedSalesOrderId: load?.salesOrderId,
          transformToLoadInput: (syncedLineItems) =>
            transformLoadFormToInput({
              form: { ...data, lineItems: syncedLineItems },
              tab,
              onlyDatesAndEntities,
              tagToLineItemMap,
            }),
          getSalesOrderParams: (syncedLineItems) =>
            transformLoadFormToSalesOrderUpdateParams({
              form: { ...data, lineItems: syncedLineItems },
              salesOrderLineItems: salesOrderLineItems ?? [],
              queriedSalesOrderId,
            }),
        });

        // Update baseline to current form state after successful save
        setDiscardBaseline(updatedForm);

        // Signal successful save for status indicator
        setShowSaveSuccess(true);

        // Handle container linking/unlinking
        const previousContainerId = load?.container?.id ?? null;
        const newContainerId = data.containerId ?? null;

        if (previousContainerId !== newContainerId) {
          // Unlink old container if it was linked
          if (previousContainerId) {
            try {
              await unlinkContainerFromLoad({ id: previousContainerId });
            } catch (err) {
              console.warn('Failed to unlink previous container:', err);
            }
          }

          // Link new container if one is selected
          if (newContainerId) {
            try {
              await linkContainerToLoad({
                id: newContainerId,
                loadId,
                sealNumber: data.sealNumber ?? null,
              });
            } catch (err) {
              console.warn('Failed to link container to load:', err);
              toast.error('Container saved but failed to link container');
            }
          }
        }

        toast.success(t.loadDetail.messages.updateSuccess);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t.loadDetail.messages.updateError,
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [
      loadId,
      syncAndSaveLoad,
      load?.salesOrderId,
      load?.container?.id,
      setDiscardBaseline,
      setShowSaveSuccess,
      toast,
      t.loadDetail.messages.updateSuccess,
      t.loadDetail.messages.updateError,
      tagToLineItemMap,
      salesOrderLineItems,
      queriedSalesOrderId,
      unlinkContainerFromLoad,
      linkContainerToLoad,
    ],
  );

  const handleRemoveOrphanedLink = React.useCallback(async () => {
    if (!orphanedLink) return;

    try {
      await deleteRelationship({
        bookingId: orphanedLink.bookingId,
        salesOrderId: orphanedLink.salesOrderId,
      });
      setOrphanedLink(null);
      toast.success(t.loadDetail.messages.orphanedLinkRemoved);
    } catch (error) {
      handleActionableError(error, toast, navigate);
    }
  }, [orphanedLink, deleteRelationship, setOrphanedLink, toast, t, navigate]);

  const handleBeforeChangeTab = React.useCallback(
    (newTab: LoadDetailsTab) => {
      if (hasUnsavedChanges) {
        setPendingTabChange(newTab);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm(discardBaseline);
      setTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm, setTab]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const notificationBanner = React.useMemo(
    () => notificationBannerByLoadStatus({ load, t }),
    [load, t],
  );

  // Photo tab title with counter badge
  const PhotoTabTitle = React.useMemo(() => {
    if (!load?.bookingId || photoRequirementsCount === 0) {
      return t.loadDetail.tabs.photos;
    }

    const badgeColor = {
      incomplete: 'bg-failure text-white',
      partial: 'bg-warning/80 text-white',
      complete: 'bg-success text-white',
    }[photoUploadStatus];

    return (
      <div className="flex items-center gap-2">
        <span>{t.loadDetail.tabs.photos}</span>
        <span
          className={cn(
            'flex h-5 w-9 items-center justify-center gap-1 rounded px-1 text-center text-xs text-abs-white',
            badgeColor,
          )}
        >
          <span>{uploadedPhotosCount}</span>
          <span>/</span>
          <span>{photoRequirementsCount}</span>
        </span>
      </div>
    );
  }, [
    load?.bookingId,
    photoRequirementsCount,
    uploadedPhotosCount,
    photoUploadStatus,
    t,
  ]);

  /*
   * Measure header dimensions for modal positioning
   */
  const handleHeaderRectChange = React.useCallback(
    (rect: ElementRect) => {
      setLoadHeaderRect(rect);
    },
    // We need to re-run this callback when the load changes
    [setLoadHeaderRect, load, isSyncingSalesOrderLineItems],
  );

  const headerRef = useElementRect<HTMLDivElement>(handleHeaderRectChange);

  const PhotosContent = React.useCallback(
    () => <LoadTabPhotos load={load} />,
    [load],
  );

  if (isLoading || isSyncingSalesOrderLineItems) {
    return (
      <SalesRootLayout>
        <div className="size-4">
          <Loading />
        </div>
      </SalesRootLayout>
    );
  }

  if (error || !load) {
    return (
      <SalesRootLayout>
        <div className="p-4 text-failure">
          {t.loadDetail.error(error?.message || 'Load not found')}
        </div>
      </SalesRootLayout>
    );
  }

  return (
    <SalesRootLayout className="@LoadDetailView">
      <EntityDetails
        headerRef={headerRef}
        onChangeTab={(tab) => setTab(tab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={tab}
        onBackPressed={handleBack}
        primaryTitle={t.loadDetail.title(load.loadNumber)}
        subtitle={
          <Row gap1>
            {t.loadDetail.created}{' '}
            <LocalizedDate
              date={load.timestamps.createdAt}
              dateStyle="medium"
            />
          </Row>
        }
        badges={
          <div className="flex flex-row items-center gap-2">
            {load.salesOrderId && (
              <SalesOrderBadge
                salesOrderNumber={load.salesOrder?.documentNumber}
                counterPartyPurchaseOrderNumber={
                  load.salesOrder?.counterPartyPurchaseOrderNumber ?? undefined
                }
              />
            )}
            {load.bookingId && (
              <BookingBadge bookingNumber={load.bookingNumber ?? undefined} />
            )}
            <LoadStatusBadge status={load.status} isReviewed={load.reviewed} />
          </div>
        }
        headerActions={
          <LoadStatusTransition
            load={load}
            disabled={isUpdating}
            // If tab is undefined, we are in the load-info tab because it is the default tab
            skipMaterialsReconciliation={
              tab === 'load-info' || tab === undefined
            }
          />
        }
        notificationBanner={notificationBanner}
        tabs={{
          'load-info': {
            title: t.loadDetail.tabs.loadAndShipmentInfo,
            content: () => (
              <LoadTabShipmentInfo
                load={load}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          materials: {
            title: (
              <div className="flex items-center gap-2">
                {t.loadDetail.tabs.materials} <MaterialsTabSize />
              </div>
            ),
            content: () => (
              <LoadTabMaterialsInfo
                load={load}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          photos: {
            title: PhotoTabTitle,
            content: PhotosContent,
          },
          documents: {
            title: t.loadDetail.tabs.documents,
            content: () => <LoadTabDocuments load={load} />,
          },
          notes: {
            title: t.loadDetail.tabs.notes,
            content: () => <LoadTabNotes load={load} />,
          },
        }}
      />

      {/* Orphaned link dialog */}
      {orphanedLink && (
        <RemoveOrphanedLinkDialog
          orphanedLink={orphanedLink}
          open={!!orphanedLink}
          onRemove={handleRemoveOrphanedLink}
          onCancel={() => setOrphanedLink(null)}
        />
      )}

      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.loadDetail.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </SalesRootLayout>
  );
};
